import React from "react";
import {
  useFormContext,
  Controller,
  FieldErrors,
  FieldValues,
} from "react-hook-form";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
interface FormDatePickerProps {
  name: string;
  label: any;
  optionLabel?: string;
  optionLabelWidth?: any;
  unMaskView?: boolean;
  required?: boolean;
  hideRequiredMark?: boolean;
  minDate?: Date;
  maxDate?: Date;
  defaultValue?: Date | any;
  disabled?: boolean;
  placeholder?: string;
  customValidation?: { validate: (selectedDate: Date) => boolean | string };
  scrollableYearDropdown?: boolean;
  showYearDropdown?: boolean;
  onChange?: (date: Date) => void;
  dateFormat?: string;
  allowClear?: boolean;
  showTime?: boolean;
}
const FormDatePicker: React.FC<FormDatePickerProps> = ({
  name,
  label,
  minDate,
  maxDate,
  required,
  hideRequiredMark,
  optionLabel,
  optionLabelWidth = "34px",
  unMaskView = false,
  defaultValue,
  disabled,
  placeholder,
  customValidation,
  scrollableYearDropdown,
  showYearDropdown,
  dateFormat,
  onChange,
  allowClear,
  showTime = false,
}) => {
  const resolvedFormat = dateFormat || (showTime ? "DD-MMM-YYYY hh:mm A" : "DD-MMM-YYYY");
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const errorMessage = getErrorMessage(errors, name);
  const disabledDate = (current: any) => {
    const minDay = minDate ? dayjs(minDate) : null;
    const maxDay = maxDate ? dayjs(maxDate) : null;
    if (minDay && current && current.isBefore(minDay, "day")) {
      return true;
    }
    if (maxDay && current && current.isAfter(maxDay, "day")) {
      return true;
    }
    return false;
  };
  const disabledDateTime = (current: any) => {
    if (!showTime) return {};
    const minDay = minDate ? dayjs(minDate) : null;
    if (minDay && current && current.isSame(minDay, "day")) {
      const minHour = minDay.hour();
      const minMinute = minDay.minute();
      return {
        disabledHours: () =>
          Array.from({ length: 24 }, (_, i) => i).filter((h) => h < minHour),
        disabledMinutes: (selectedHour: number) =>
          selectedHour === minHour
            ? Array.from({ length: 60 }, (_, i) => i).filter(
                (m) => m < minMinute
              )
            : [],
      };
    }
    const maxDay = maxDate ? dayjs(maxDate) : null;
    if (maxDay && current && current.isSame(maxDay, "day")) {
      const maxHour = maxDay.hour();
      const maxMinute = maxDay.minute();
      return {
        disabledHours: () =>
          Array.from({ length: 24 }, (_, i) => i).filter((h) => h > maxHour),
        disabledMinutes: (selectedHour: number) =>
          selectedHour === maxHour
            ? Array.from({ length: 60 }, (_, i) => i).filter(
                (m) => m > maxMinute
              )
            : [],
      };
    }
    return {};
  };
  return (
    <div
      className={`form-group ${optionLabel ? "mb-1" : ""} ${
        unMaskView ? "unmask-view" : ""
      }`}
    >
      {label && (
        <label className="form-label" htmlFor={name}>
          {label}
          {required && !hideRequiredMark ? (
            <span className="mandatory">*</span>
          ) : (
            ""
          )}
        </label>
      )}
      <div className="form-control-wrap">
        <div
          className={`form-control-select ${
            optionLabel ? "d-flex align-items-center" : ""
          }`}
        >
          {optionLabel && (
            <label
              className={`form-label`}
              style={{ minWidth: optionLabelWidth }}
              htmlFor={name}
            >
              {optionLabel}
            </label>
          )}
          <Controller
            name={name}
            control={control}
            rules={{
              required:
                required && !hideRequiredMark
                  ? "This field is required"
                  : undefined,
              validate: customValidation
                ? customValidation.validate
                : undefined,
            }}
            render={({ field }) => (
              <DatePicker
                id={name}
                className="form-control datepicker"
                value={field.value}
                onChange={(date: any) => {
                  field.onChange(date);
                  if (onChange) {
                    onChange(date);
                  }
                }}
                format={resolvedFormat}
                disabled={disabled}
                placeholder={placeholder || `Select ${label}`}
                disabledDate={disabledDate}
                disabledTime={showTime ? disabledDateTime : undefined}
                showTime={showTime ? { use12Hours: true, format: "hh:mm A" } : false}
                size="small"
                variant="filled"
                inputReadOnly={true}
                allowClear={allowClear || !required}
              />
            )}
          />
          {errorMessage && !optionLabel && (
            <p className="mandatory">{errorMessage}</p>
          )}
        </div>
      </div>
      {errorMessage && optionLabel && (
        <p className="mandatory">{errorMessage}</p>
      )}
    </div>
  );
};
function getErrorMessage<T extends FieldValues>(
  errors: FieldErrors<T>,
  fieldName: string
): string | undefined {
  const parts = fieldName.split(/[\[\].]+/).filter(Boolean); // This splits on brackets and dots, and removes empty strings
  let currentError: any = errors; // Start with the whole errors object
  for (const part of parts) {
    if (currentError && typeof currentError === "object") {
      currentError = currentError[part]; // Navigate deeper into the errors object
    } else {
      return undefined; // No error found for this part
    }
  }
  if (
    currentError &&
    "message" in currentError &&
    typeof currentError.message === "string"
  ) {
    return currentError.message;
  }
  return undefined; // No message found
}
export default FormDatePicker;
